/**
 * Converts an ISO 8601 timestamp string to a human-readable relative time string.
 *
 * Uses the largest appropriate unit:
 * - Less than 1 minute → "just now"
 * - 1-59 minutes → "X minutes ago" (or "1 minute ago")
 * - 1-23 hours → "X hours ago" (or "1 hour ago")
 * - 1-6 days → "X days ago" (or "1 day ago")
 * - 1-4 weeks → "X weeks ago" (or "1 week ago")
 * - 1-11 months → "X months ago" (or "1 month ago")
 * - 12+ months → "X years ago" (or "1 year ago")
 *
 * @param isoTimestamp - An ISO 8601 formatted timestamp string
 * @param now - Optional current time for testability (defaults to Date.now())
 * @returns A human-readable relative time string
 */
export function formatRelativeTime(isoTimestamp: string, now?: Date): string {
  const timestamp = new Date(isoTimestamp);
  const currentTime = now ?? new Date();
  const diffMs = currentTime.getTime() - timestamp.getTime();

  // Handle future timestamps or zero difference
  if (diffMs < 0) {
    return 'just now';
  }

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years >= 1) {
    return years === 1 ? '1 year ago' : `${years} years ago`;
  }

  if (months >= 1) {
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }

  if (weeks >= 1) {
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }

  if (days >= 1) {
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }

  if (hours >= 1) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }

  if (minutes >= 1) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  }

  return 'just now';
}
